import {useEffect, useState, useCallback} from 'react';
import {kr} from '../lib/format.js';
import {api} from '../lib/auth.js';

/**
 * The drill-down under one period row: that period as a Resultatrapport (ÅRL), the same
 * sections the accounting system prints and the same ones the budget workbook uses, so
 * a figure here and a figure there are the same figure. `month` is the period key the
 * table holds — `2026-08`, `2026-Q3`, `2026-H2` or `2026` — and the API unrolls it.
 *
 * SIGN: report convention — revenue positive, costs negative. The ledger stores credit
 * negative; the flip happens once, server-side, and nowhere else.
 *
 * Vouchers are fetched per account ON CLICK, never with the report. A month has
 * hundreds of vouchers and almost none belong to an account anybody opens.
 */
export default function MonthBreakdown({month, label, columnCount}) {
	const [data, setData] = useState(null);
	const [error, setError] = useState(null);
	const [openGroups, setOpenGroups] = useState(() => new Set());
	const [openAccount, setOpenAccount] = useState(null);
	const [vouchers, setVouchers] = useState({});
	const [invoices, setInvoices] = useState(null);
	const [showInvoices, setShowInvoices] = useState(false);

	useEffect(() => {
		let live = true;
		api(`/api/report/${month}`).then(r => r.json())
			.then(d => { if (live) (d.error ? setError(d.error) : setData(d)); })
			.catch(e => live && setError(e.message));
		return () => { live = false; };
	}, [month]);

	const toggleAccount = useCallback(account => {
		setOpenAccount(prev => (prev === account ? null : account));
		setVouchers(prev => {
			if (prev[account]) return prev;   // already fetched or in flight
			api(`/api/report/${month}/account/${account}`).then(r => r.json())
				.then(d => setVouchers(c => ({...c, [account]: {loading: false, list: d.vouchers ?? []}})))
				.catch(() => setVouchers(c => ({...c, [account]: {loading: false, list: [], error: true}})));
			return {...prev, [account]: {loading: true, list: []}};
		});
	}, [month]);

	const toggleInvoices = () => {
		setShowInvoices(v => !v);
		if (!invoices) {
			api(`/api/report/${month}/invoices`).then(r => r.json())
				.then(setInvoices).catch(() => setInvoices([]));
		}
	};

	const cell = v => <span className={v < 0 ? 'md__neg' : undefined}>{kr(v)}</span>;

	return (
		<tr className="md__row">
			<td colSpan={columnCount} className="md">
				{error && <div className="state">Kunde inte hämta rapporten: {error}</div>}
				{!data && !error && <div className="state">Hämtar…</div>}
				{data && (
					<>
						<div className="md__head">
							<span className="md__title">Resultatrapport {label ?? month}</span>
							<button className="btn" onClick={toggleInvoices}>
								{showInvoices ? 'Dölj fakturor' : 'Visa fakturor'}
							</button>
						</div>
						<table className="md__table">
							<tbody>
								{data.groups.map(g => {
									const open = openGroups.has(g.key);
									const sub = data.subtotals.filter(s => s.after === g.key);
									return [
										<tr key={g.key} className="md__group" onClick={() => setOpenGroups(s => {
											const n = new Set(s); n.has(g.key) ? n.delete(g.key) : n.add(g.key); return n;
										})}>
											<td>{open ? '−' : '+'} {g.label}</td>
											<td className="md__amount">{cell(g.total)}</td>
										</tr>,
										...(open ? g.accounts.map(a => [
											<tr key={`${g.key}-${a.account}`} className="md__account"
												onClick={() => toggleAccount(a.account)}>
												<td>{a.account} {a.name}
													<span className="md__count">{a.voucher_count} ver</span></td>
												<td className="md__amount">{cell(a.amount)}</td>
											</tr>,
											...(openAccount === a.account ? [(
												<tr key={`${g.key}-${a.account}-v`}>
													<td colSpan={2} className="md__vouchers">
														{vouchers[a.account]?.loading && <span>Hämtar verifikat…</span>}
														{vouchers[a.account]?.error && <span>Kunde inte hämta verifikaten.</span>}
														{vouchers[a.account] && !vouchers[a.account].loading && (
															<table className="md__table">
																<tbody>
																	{vouchers[a.account].list.map(v => (
																		<tr key={v._id}>
																			<td className="md__vnum">{v.series}{v.number}</td>
																			<td>{v.date}</td>
																			<td>{v.description}</td>
																			<td className="md__amount">{cell(v.amount)}</td>
																		</tr>
																	))}
																</tbody>
															</table>
														)}
													</td>
												</tr>
											)] : []),
										]).flat() : []),
										...sub.map(s => (
											<tr key={s.key} className="md__subtotal">
												<td>{s.label}</td>
												<td className="md__amount">{cell(s.amount)}</td>
											</tr>
										)),
									];
								}).flat()}
							</tbody>
						</table>
						{showInvoices && (
							<div className="md__invoices">
								<div className="md__title">Fakturor utställda i {label ?? month}</div>
								{!invoices && <span>Hämtar…</span>}
								{invoices?.length === 0 && <span>Inga fakturor i perioden.</span>}
								{invoices?.length > 0 && (
									<table className="md__table">
										<tbody>
											{invoices.map(i => (
												<tr key={i._id}>
													<td className="md__vnum">{i._id}</td>
													<td>{i.InvoiceDate}</td>
													<td>{i.CustomerName}</td>
													<td>{i.InvoicePeriodStart
														? `${i.InvoicePeriodStart} – ${i.InvoicePeriodEnd}` : '—'}</td>
													<td>{i.Booked ? 'bokförd' : 'ej bokförd'}</td>
													<td className="md__amount">{kr(i.Total)}</td>
												</tr>
											))}
										</tbody>
									</table>
								)}
							</div>
						)}
					</>
				)}
			</td>
		</tr>
	);
}
